import { Component, Suspense, lazy } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation } from "react-router";
import { ToastProvider, PageLoader } from "../ui/components.tsx";
import { AppStateProvider, RequireAuth, useAppState } from "./state.tsx";
import { Layout } from "./core/Layout.tsx";
import { isBookiesHost } from "../shared/bookies.ts";
const BookiePortal = lazy(() => import("./shop/BookiePortal.tsx"));

// Each product area owns its own nested <Routes>; areas are code-split.
const CoreArea = lazy(() => import("./core/CoreArea.tsx"));
const CasinoArea = lazy(() => import("./casino/CasinoArea.tsx"));
const FairnessArea = lazy(() => import("./casino/FairnessArea.tsx"));
const SportsArea = lazy(() => import("./sports/SportsArea.tsx"));
const DrawsArea = lazy(() => import("./draws/DrawsArea.tsx"));
const ShopDashboard = lazy(() => import("./shop/ShopDashboard.tsx"));

/**
 * Keeps the shell (header, category bar, footer) up when a page fails to render or its code
 * can't be loaded (e.g. a stale chunk after a deploy): the page area offers a reload instead.
 */
class AreaBoundary extends Component<{ resetKey: string; children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    console.error("Page failed to render", error, info.componentStack);
  }

  override componentDidUpdate(prev: { resetKey: string }): void {
    if (prev.resetKey !== this.props.resetKey && this.state.failed) this.setState({ failed: false });
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="page">
        <div className="page-card wb-light page-card--narrow" role="alert">
          <p className="page-card__title">This page didn't load.</p>
          <div>
            <button type="button" className="btn btn-primary btn-cta" onClick={() => window.location.reload()}>
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}

function AreaRoutes() {
  const { pathname } = useLocation();
  const { user, sessionLoaded, shopMode, shopSessionClosed, clearShopSessionClosed } = useAppState();
  if (!sessionLoaded) return <PageLoader />;
  if (shopSessionClosed && pathname !== '/login') return <div className="page"><section className="page-card wb-light page-card--narrow">
    <h1 className="page-card__title">Play session closed</h1><p>{shopSessionClosed}</p><p>Any remaining balance stays on this account. Ask your cashier to top up or pay it out.</p>
    <Link className="btn btn-primary" to="/login?next=/casino" onClick={clearShopSessionClosed}>Return to player login</Link>
  </section></div>;
  const shopHome = user?.shopOwnerId ? '/shop' : '/casino';
  if (shopMode && (pathname === '/' || /^\/(sports|horse-racing)(\/|$)/.test(pathname) || pathname === '/complete-profile' || (pathname === '/wallet' || /^\/wallet\/(deposit|withdraw|bank)/.test(pathname)) || (user?.shopPlayer && /^\/account(\/profile|\/verification|\/security)?\/?$/.test(pathname)))) return <Navigate to={user ? shopHome : '/login?next=/casino'} replace />;
  if (shopMode && !user && !['/login', '/help', '/support', '/responsible-gambling', '/terms', '/privacy', '/licensing'].some(path => pathname === path || pathname.startsWith(`${path}/`))) return <Navigate to="/login?next=/casino" replace />;
  return (
    <AreaBoundary resetKey={pathname}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/shop" element={<RequireAuth><ShopDashboard /></RequireAuth>} />
          <Route path="/casino/*" element={<CasinoArea />} />
          <Route path="/fairness/*" element={<FairnessArea />} />
          <Route path="/sports/*" element={<SportsArea />} />
          <Route path="/horse-racing/*" element={<DrawsArea />} />
          <Route path="/lucky-numbers/*" element={<DrawsArea />} />
          <Route path="/*" element={<CoreArea />} />
        </Routes>
      </Suspense>
    </AreaBoundary>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppStateProvider>
        <ToastProvider>
          {isBookiesHost(window.location.hostname) || window.location.pathname === '/bookies' ? <Suspense fallback={<PageLoader />}><BookiePortal /></Suspense> : <Layout>
            <AreaRoutes />
          </Layout>}
        </ToastProvider>
      </AppStateProvider>
    </BrowserRouter>
  );
}
